import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { applyTheme, normalizeTheme } from "../lib/themes";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [company, setCompany] = useState(null);
  const [platform, setPlatform] = useState({ name: "WorkZone", tagline: "إدارة أذكى • أداء أفضل", logo_id: null });
  const [loading, setLoading] = useState(true);

  const refreshCompany = useCallback(async () => {
    try { setCompany(await api.getCompany()); } catch { setCompany(null); }
  }, []);

  const refreshPlatform = useCallback(async () => {
    try { const p = await api.getPlatform(); setPlatform(p); applyTheme(normalizeTheme(p.theme)); } catch { /* keep default brand */ }
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      setSubscription(await api.subscription());
    } catch {
      setSubscription(null);
    }
  }, []);

  const seq = useRef(0);
  const checkAuth = useCallback(async () => {
    const my = ++seq.current;
    const stale = () => my !== seq.current;
    try {
      const u = await api.me();
      if (stale()) return;
      setUser(u);
      await refreshSubscription();
      refreshCompany();
    } catch {
      // Not a company user — maybe an employee-portal session
      try {
        const p = await api.portalMe();
        if (stale()) return;
        setUser({ kind: "employee", ...p.employee, company: p.company });
        setSubscription(null);
      } catch {
        // Maybe a marketer session
        try {
          const m = await api.marketerMe();
          if (stale()) return;
          setUser({ kind: "marketer", ...m });
          setSubscription(null);
        } catch {
          if (stale()) return;
          setUser(null);
          setSubscription(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [refreshSubscription, refreshCompany]);

  const orgKey = user?.kind ? "" : user?.org_id || "";
  useEffect(() => {
    setCompany(null);
    if (orgKey) refreshCompany();
  }, [orgKey, refreshCompany]);

  useEffect(() => {
    refreshPlatform();
    // If returning from OAuth callback, skip the /me check; AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth, refreshPlatform]);

  const logout = async () => {
    try {
      if (user?.kind === "employee") await api.portalLogout();
      else if (user?.kind === "marketer") await api.marketerLogout();
      else await api.logout();
    } catch { /* ignore */ }
    setUser(null);
    setSubscription(null);
    setCompany(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, subscription, refreshSubscription, company, refreshCompany, platform, refreshPlatform, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
